import React, {createContext, useContext, ReactNode} from 'react'

/**
 * Imperative handles for the two lateral panels owned by MasterLayout.
 *
 * This started life as a chat-only context; the Files panel needs the same shape (components far
 * from the layout must be able to open it), so it was generalised in place. `useChat()` keeps its
 * original surface so existing callers are unaffected.
 */
interface LayoutPanelsContextType {
  openChat: () => void
  isChatVisible: boolean
  toggleChat: () => void
  isFilesPanelOpen: boolean
  toggleFilesPanel: () => void
  openFilesPanel: () => void
  /** Open the panel and navigate the file browser to `relativePath` (no leading slash). */
  openFilesPanelAt: (relativePath: string) => void
  closeFilesPanel: () => void
  /**
   * Bring back the centre column (Dashboard/Jobs/Results) after a Files+Chat focus collapse.
   * No-op when the centre is already visible. Safe to call from top-nav clicks on the current route.
   */
  expandCenter: () => void
}

const LayoutPanelsContext = createContext<LayoutPanelsContextType | undefined>(undefined)

interface ChatProviderProps {
  children: ReactNode
  onOpenChat: () => void
  isChatVisible: boolean
  onToggleChat: () => void
  isFilesPanelOpen: boolean
  onToggleFilesPanel: () => void
  onOpenFilesPanel: (relativePath?: string) => void
  onCloseFilesPanel: () => void
  onExpandCenter: () => void
}

export const ChatProvider: React.FC<ChatProviderProps> = ({
  children,
  onOpenChat,
  isChatVisible,
  onToggleChat,
  isFilesPanelOpen,
  onToggleFilesPanel,
  onOpenFilesPanel,
  onCloseFilesPanel,
  onExpandCenter,
}) => {
  const value: LayoutPanelsContextType = React.useMemo(
    () => ({
      openChat: onOpenChat,
      isChatVisible,
      toggleChat: onToggleChat,
      isFilesPanelOpen,
      toggleFilesPanel: onToggleFilesPanel,
      openFilesPanel: () => onOpenFilesPanel(),
      openFilesPanelAt: (relativePath: string) => onOpenFilesPanel(relativePath),
      closeFilesPanel: onCloseFilesPanel,
      expandCenter: onExpandCenter,
    }),
    [
      onOpenChat,
      isChatVisible,
      onToggleChat,
      isFilesPanelOpen,
      onToggleFilesPanel,
      onOpenFilesPanel,
      onCloseFilesPanel,
      onExpandCenter,
    ]
  )

  return <LayoutPanelsContext.Provider value={value}>{children}</LayoutPanelsContext.Provider>
}

export const useLayoutPanels = (): LayoutPanelsContextType => {
  const context = useContext(LayoutPanelsContext)
  if (context === undefined) {
    throw new Error('useLayoutPanels must be used within a ChatProvider')
  }
  return context
}

/** Same as {@link useLayoutPanels} but `undefined` outside ChatProvider (composer unit tests). */
export const useLayoutPanelsOptional = (): LayoutPanelsContextType | undefined =>
  useContext(LayoutPanelsContext)

/** Unchanged surface for existing chat callers. */
export const useChat = (): {openChat: () => void} => useLayoutPanels()
